import { useEffect, useRef } from 'react'
import { useSurfaceDocument, getInteractionDocument } from '../components/windows/windowSurface'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Keep keyboard focus inside a dialog and return it to the launcher on close. */
export function useDialogFocus (open = true) {
  const surfaceDocument = useSurfaceDocument()
  const dialogRef = useRef(null)
  const returnFocusRef = useRef(null)
  const wasOpenRef = useRef(false)

  // Capture the launcher during render, before React commits any autoFocus
  // inside the dialog. Capturing in useEffect is too late for such dialogs.
  if (open && !wasOpenRef.current && typeof document !== 'undefined') {
    returnFocusRef.current = (surfaceDocument === document ? getInteractionDocument() : surfaceDocument).activeElement
  }
  wasOpenRef.current = open

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined
    const dialog = dialogRef.current
    if (!dialog) return undefined

    const doc = dialog.ownerDocument
    const win = doc.defaultView
    const previousFocus = returnFocusRef.current || doc.activeElement
    const previousOverflow = doc.body.style.overflow
    doc.body.style.overflow = 'hidden'
    const focusable = () => [...dialog.querySelectorAll(FOCUSABLE)]
      .filter(element => !element.matches(':disabled') && !element.closest('[hidden], [inert], [aria-hidden="true"]'))

    const frame = win.requestAnimationFrame(() => {
      // React's autoFocus runs during commit. Preserve that deliberate target
      // instead of replacing it with the first DOM button (often “Schließen”).
      if (dialog.contains(doc.activeElement) && doc.activeElement !== dialog) return
      const target = dialog.querySelector('[data-autofocus="true"], [autofocus]') || focusable()[0] || dialog
      target.focus?.({ preventScroll: true })
    })
    const trapFocus = event => {
      if (event.key !== 'Tab') return
      const items = focusable()
      if (!items.length) {
        event.preventDefault()
        dialog.focus?.()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && (doc.activeElement === first || !dialog.contains(doc.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (doc.activeElement === last || !dialog.contains(doc.activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }
    doc.addEventListener('keydown', trapFocus)
    return () => {
      win.cancelAnimationFrame(frame)
      doc.removeEventListener('keydown', trapFocus)
      doc.body.style.overflow = previousOverflow
      if (previousFocus?.isConnected) {
        win.requestAnimationFrame(() => previousFocus.focus?.({ preventScroll: true }))
      }
      returnFocusRef.current = null
    }
  }, [open, surfaceDocument])

  return dialogRef
}

export default useDialogFocus
